'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Link from 'next/link';
import { ArrowLeft, Copy, Download, Check, FileUp } from 'lucide-react';

export default function JsonToCsvPage() {
  const [jsonInput, setJsonInput] = useState('');
  const [csvOutput, setCsvOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const convertJsonToCsv = () => {
    try {
      setError('');
      if (!jsonInput.trim()) {
        setError('Please enter JSON data');
        return;
      }

      const data = JSON.parse(jsonInput);
      
      if (!Array.isArray(data)) {
        setError('JSON must be an array of objects');
        return;
      }

      if (data.length === 0) {
        setError('JSON array is empty');
        return;
      }

      // Get all unique keys from all objects
      const allKeys = new Set<string>();
      data.forEach((obj: any) => {
        Object.keys(obj).forEach(key => allKeys.add(key));
      });
      const headers = Array.from(allKeys);

      // Create CSV
      const csvRows = [];
      csvRows.push(headers.join(','));

      data.forEach((obj: any) => {
        const values = headers.map(header => {
          const value = obj[header] ?? '';
          // Escape commas and quotes
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvRows.push(values.join(','));
      });

      setCsvOutput(csvRows.join('\n'));
    } catch (err) {
      setError('Invalid JSON format. Please check your input.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(csvOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCsv = () => {
    const blob = new Blob([csvOutput], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'converted.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setJsonInput(text);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              JSON to CSV Converter Online
            </h1>
            <p className="text-lg text-muted-foreground">
              Convert your JSON array to CSV format easily
            </p>
          </div>

          {/* File Upload Area - Full Width */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Upload JSON File</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              <FileUp className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <p className="text-lg font-medium mb-2">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .json files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">JSON Input</label>
                <Textarea
                  placeholder='[{"name":"John","age":30,"city":"New York"},{"name":"Jane","age":25,"city":"London"}]'
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <Button 
                onClick={convertJsonToCsv} 
                className="w-full"
                size="lg"
              >
                Convert to CSV
              </Button>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">CSV Output</label>
                <Textarea
                  value={csvOutput}
                  readOnly
                  placeholder="Your CSV output will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-muted"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={copyToClipboard} 
                  className="flex-1"
                  variant="outline"
                  disabled={!csvOutput}
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button 
                  onClick={downloadCsv} 
                  className="flex-1"
                  variant="outline"
                  disabled={!csvOutput}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </div>

          {/* Example */}
          <div className="mt-8 p-6 bg-card rounded-lg border">
            <h3 className="font-semibold mb-2">Example JSON:</h3>
            <pre className="text-sm bg-muted p-4 rounded overflow-x-auto">
              {`[
  {"name": "John", "age": 30, "city": "New York"},
  {"name": "Jane", "age": 25, "city": "London"},
  {"name": "Bob", "age": 35, "city": "Paris"}
]`}
            </pre>
          </div>
        </div>
      </main>

      {/* User Problems & FAQs for SEO */}
      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Common JSON to CSV Questions & Problems</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. How do I convert JSON to CSV online?</p><p className="text-muted-foreground">Paste or upload your JSON array above and click convert. The CSV will be generated instantly.</p></div>
          <div>
            <p className="font-semibold">2. What JSON format is supported?</p>
            <p className="text-muted-foreground">The tool supports an array of objects, e.g. <code>{'[{"name":"John"},{"name":"Jane"}]'}</code>.</p>
          </div>
          <div><p className="font-semibold">3. Can I download the CSV result?</p><p className="text-muted-foreground">Yes, after conversion, click the Download button to save your CSV file.</p></div>
          <div><p className="font-semibold">4. Is my data secure?</p><p className="text-muted-foreground">All conversion happens in your browser. No data is uploaded to any server.</p></div>
          <div><p className="font-semibold">5. What if my JSON is invalid?</p><p className="text-muted-foreground">The tool will show an error if your JSON is not valid or not an array of objects.</p></div>
          <div><p className="font-semibold">6. Can I convert large JSON files?</p><p className="text-muted-foreground">For very large files, browser performance may vary. For huge files, use a desktop tool.</p></div>
          <div><p className="font-semibold">7. Can I use this tool for free?</p><p className="text-muted-foreground">Yes, this online JSON to CSV converter is free to use.</p></div>
          <div><p className="font-semibold">8. Does it support nested JSON?</p><p className="text-muted-foreground">Only flat objects are supported. Nested objects will be stringified.</p></div>
          <div><p className="font-semibold">9. Can I copy the CSV output?</p><p className="text-muted-foreground">Yes, use the Copy button to copy the CSV result to your clipboard.</p></div>
          <div><p className="font-semibold">10. What browsers are supported?</p><p className="text-muted-foreground">All modern browsers are supported, including Chrome, Firefox, and Edge.</p></div>
        </div>
      </section>
      <div className="text-center text-xs text-muted-foreground pb-4">online</div>
      <Footer />
    </div>
  );
}
